import { IconButton } from '@fluentui/react';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';

import * as Styled from '../../Common/Styles/attachmentForm.styled';
import { AttachmentItem, DeleteAttachmentRequest } from '../CommonModels/Attachments';
import { getTruncatedDisplayFileName } from '../Helper/attachmentFileNameHelper';

type AttachmentsFormProps = {
  requestId: string;
  attachmentList: Array<AttachmentItem>;
  setAttachmentList(val: Array<AttachmentItem>): void;
  deleteAttachmentRequestList: Array<DeleteAttachmentRequest>;
  setDeleteAttachmentRequestList(val: Array<DeleteAttachmentRequest>): void;
};

export function AttachmentsForm(props: AttachmentsFormProps): React.ReactElement {
  const { requestId, attachmentList, setAttachmentList, deleteAttachmentRequestList, setDeleteAttachmentRequestList } =
    props;
  const [attachmentsError, setAttachmentsError] = React.useState<boolean>(false);
  const [attachmentsErrorText, setAttachmentsErrorText] = React.useState<string>('');

  const handleDeleteAttachment = (toBeDeletedAttachment: AttachmentItem) => {
    // Remove attachment from the attachment list
    const filteredAttachmentList = attachmentList.filter((attachmentItem: AttachmentItem) => {
      return attachmentItem !== toBeDeletedAttachment;
    });
    setAttachmentsErrorText('');
    setAttachmentsError(false);
    setAttachmentList(filteredAttachmentList);

    // If the attachment is already uploaded, save it for later to be deleted for a request submission
    if (toBeDeletedAttachment.fileUri) {
      const newDeleteAttachmentRequest: DeleteAttachmentRequest = {
        folderName: toBeDeletedAttachment.folderName,
        fileName: toBeDeletedAttachment.fileName,
      };
      const newDeleteAttachmentRequestList: DeleteAttachmentRequest[] = deleteAttachmentRequestList;
      newDeleteAttachmentRequestList.push(newDeleteAttachmentRequest);
      setDeleteAttachmentRequestList(newDeleteAttachmentRequestList);
    }


  };

  const getDisplayFileName = (attachmentFileName: string): string => {
    const maxFileNameLength = 50;
    return getTruncatedDisplayFileName(attachmentFileName, maxFileNameLength);
  };

  const fetchFileSizeFromUrl = async (fileUri: string) => {
    return new Promise<number>((resolve) => {
      fetch(fileUri, {
        method: 'HEAD',
      })
        .then((res) => {
          resolve(Number(res.headers.get('content-length')));
        })
        .catch(() => {
          setAttachmentsErrorText('An error occurred while uploading attachments, please contact the Admin.');
          setAttachmentsError(true);
        });
    });
  };

  const getTotalSizeOfAttachmentList = async () => {
    return new Promise<number>(async (resolve) => {
      let currentTotalSize = 0;
      for (const attachmentItem of attachmentList) {
        if (attachmentItem.fileUri) {
          const currentFileSize: number = (await fetchFileSizeFromUrl(attachmentItem.fileUri)) as number;
          currentTotalSize = currentTotalSize + currentFileSize;
        }
        if (attachmentItem.fileDatabase64) {
          currentTotalSize = currentTotalSize + attachmentItem.fileSize;
        }
      }
      resolve(currentTotalSize);
    });
  };

  const getFileNameSetOfAttachmentList = (): Set<string> => {
    const fileNameSet = new Set<string>();
    for (const attachmentItem of attachmentList) {
      fileNameSet.add(attachmentItem.fileName);
    }
    return fileNameSet;
  };

  // Read attachment as Base64
  function readFile(file: Blob) {
    return new Promise(function (resolve) {
      const reader = new FileReader();
      reader.onloadend = function () {
        resolve(reader.result);
      };
      reader.readAsDataURL(file);
    });
  }

  const onDropChange = async (acceptedFiles: File[]) => {
    let duplicateFileNameFlag = false;
    let maxSizeFlag = false;

    const totalMaxSize = 8000000; // 8 MB
    const newAttachmentList: AttachmentItem[] = [];
    let currentTotalSize: number = (await getTotalSizeOfAttachmentList()) as number;
    const fileNameSet: Set<string> = getFileNameSetOfAttachmentList();

    // Iterate over the newly attached files
    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i];
      // Check for files that exceed the total max size
      const newFileSize: number = file.size;
      currentTotalSize = currentTotalSize + newFileSize;

      if (currentTotalSize > totalMaxSize) {
        maxSizeFlag = true;
        break;
      }

      // Check for duplicate file names, including files within the same drop
      const newFileName = file['name'];
      if (fileNameSet.has(newFileName)) {
        duplicateFileNameFlag = true;
        break;
      }
      fileNameSet.add(newFileName);

      const base64File = await readFile(file);
      const base64String = base64File.toString().split(',')[1];
      const newAttachment: AttachmentItem = {
        folderName: requestId,
        fileName: newFileName,
        fileDatabase64: base64String,
        fileSize: newFileSize,
      };
      newAttachmentList.push(newAttachment);
    }

    if (acceptedFiles.length === 0) {
      setAttachmentsErrorText('Please upload at least one acceptable new file.');
      setAttachmentsError(true);
    } else if (duplicateFileNameFlag) {
      setAttachmentsErrorText('Please upload files with unique file names.');
      setAttachmentsError(true);
    } else if (maxSizeFlag) {
      setAttachmentsErrorText('Please upload files that do not exceed 8 MB.');
      setAttachmentsError(true);
    } else {
      const newConcatAttachmentList = attachmentList.concat(newAttachmentList);
      setAttachmentsErrorText('');
      setAttachmentsError(false);
      setAttachmentList(newConcatAttachmentList);
    }
  };

  // Dropzone Hook
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onDropChange,
  });

  return (
    <div>
      <Styled.DropzoneContainer data-testid="attachments-form-dropzone" {...getRootProps()}>
        <input {...getInputProps()} />
        <Styled.DropzoneTextStyled>Drag and drop files here, or click to select files.</Styled.DropzoneTextStyled>
      </Styled.DropzoneContainer>

      {attachmentsError && <Styled.ErrorTextStyled>{attachmentsErrorText}</Styled.ErrorTextStyled>}

      {attachmentList?.length > 0 && (
        <Styled.AttachmentListContainer>
          {attachmentList?.map((attachmentItem: AttachmentItem) => (
            <Styled.AttachmentContainer key={attachmentItem.fileName}>
              <Styled.AttachmentTextStyled>{getDisplayFileName(attachmentItem.fileName)}</Styled.AttachmentTextStyled>
              <IconButton
                id={attachmentItem.fileName}
                iconProps={{ iconName: 'Cancel' }}
                ariaLabel="Delete Icon Button"
                onClick={() => {
                  handleDeleteAttachment(attachmentItem);
                }}
                styles={Styled.deleteButtonStyles}
              />
            </Styled.AttachmentContainer>
          ))}
        </Styled.AttachmentListContainer>
      )}
    </div>
  );
}
